import MODULE_REGISTRY from "@/modules";
import { IConfig, ITabConfig } from "@/type/router";

/**
 * Find a page config by its key (recursive search through children)
 * @param configs - Array of IConfig to search through
 * @param key - The key to search for
 * @returns The matching IConfig or null if not found
 */
export function findPageConfig(configs: IConfig[], key: string): IConfig | null {
  for (const config of configs) {
    if (config.key === key) return config;
    if (config.children) {
      const found = findPageConfig(config.children, key);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Flatten config tree to a map of page key -> path
 * Only includes items with both path and a registered component (actual navigable pages)
 * @param configs - Array of IConfig to flatten
 * @returns Record of page key to path
 */
export function buildPagePathMap(configs: IConfig[]): Record<string, string> {
  const map: Record<string, string> = {};

  function traverse(items: IConfig[]) {
    for (const item of items) {
      if (item.path && MODULE_REGISTRY[item.key]?.component) {
        map[item.key] = item.path;
      }
      if (item.children) {
        traverse(item.children);
      }
    }
  }

  traverse(configs);
  return map;
}

/**
 * Get all valid tab keys for a specific page
 * @param configs - Array of IConfig to search through
 * @param pageKey - The page key to get tabs for
 * @returns Array of tab keys, empty array if page not found or has no tabs
 */
export function getPageTabKeys(configs: IConfig[], pageKey: string): string[] {
  const pageConfig = findPageConfig(configs, pageKey);
  return pageConfig?.tabs?.map(tab => tab.key) ?? [];
}

/**
 * Get tabs configuration for a specific page
 * @param configs - Array of IConfig to search through
 * @param pageKey - The page key to get tabs for
 * @returns Array of ITabConfig, empty array if page not found or has no tabs
 */
export function getPageTabs(configs: IConfig[], pageKey: string): ITabConfig[] {
  const pageConfig = findPageConfig(configs, pageKey);
  return pageConfig?.tabs ?? [];
}

/**
 * Get all pages with their associated tab keys
 * Useful for generating documentation or validation
 * @param configs - Array of IConfig to process
 * @returns Record of page key to array of tab keys
 */
export function getAllPagesWithTabs(configs: IConfig[]): Record<string, string[]> {
  const result: Record<string, string[]> = {};

  function traverse(items: IConfig[]) {
    for (const item of items) {
      if (MODULE_REGISTRY[item.key]?.component) {
        result[item.key] = item.tabs?.map(tab => tab.key) ?? [];
      }
      if (item.children) {
        traverse(item.children);
      }
    }
  }

  traverse(configs);
  return result;
}

/**
 * Get all navigable page keys from config
 * @param configs - Array of IConfig to process
 * @returns Array of page keys that have a registered component (navigable pages)
 */
export function getAllPageKeys(configs: IConfig[]): string[] {
  const keys: string[] = [];

  function traverse(items: IConfig[]) {
    for (const item of items) {
      if (MODULE_REGISTRY[item.key]?.component && item.path) {
        keys.push(item.key);
      }
      if (item.children) {
        traverse(item.children);
      }
    }
  }

  traverse(configs);
  return keys;
}

/**
 * Catalog entry for a single navigable page. Used by useCopilotPageContext
 * to expose intent-discoverable page metadata to the Copilot AI.
 */
export interface PageCatalogEntry {
  /** Stable page identifier (e.g. "TEST_SPEC") */
  pageKey: string;
  /** URL path */
  path: string;
  /** i18n key (already enriched from MODULE_REGISTRY) */
  label: string;
  /** One-sentence purpose of the page; undefined when backend has not populated it */
  briefDescription?: string;
}

/**
 * Flatten the menu tree into a list of navigable pages with brief metadata.
 * Same filter as buildPagePathMap (must have both path and a registered
 * component) — category-only nodes like ROOT and DEFINE are excluded.
 *
 * @param configs - Array of IConfig (typically from Redux state.router.menuConfig)
 * @returns Array of PageCatalogEntry, one per navigable leaf
 */
export function buildPageCatalog(configs: IConfig[]): PageCatalogEntry[] {
  const out: PageCatalogEntry[] = [];

  function traverse(items: IConfig[]) {
    for (const item of items) {
      if (item.path && MODULE_REGISTRY[item.key]?.component) {
        out.push({
          pageKey: item.key,
          path: item.path,
          label: item.label,
          briefDescription: item.briefDescription,
        });
      }
      if (item.children) {
        traverse(item.children);
      }
    }
  }

  traverse(configs);
  return out;
}
